import { jsPDF } from "jspdf";
import { ADORNMENTS } from "@/lib/adornments";
import { HANDS_GRATITUDE, PALMISTRY_RESPECT } from "@/lib/lore";

// ---------------------------------------------------------------------------
// Downloadable keepsake artifacts: a pretty share/keepsake IMAGE (PNG) and an
// in-depth PDF of the full reading. Replaces the old emailed keepsake.
// ---------------------------------------------------------------------------

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text).split(" ");
  let line = "";
  let yy = y;
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, yy);
      line = w;
      yy += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, yy);
  return yy;
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function slug(name) {
  return name?.trim() ? name.trim().replace(/\s+/g, "-").toLowerCase() : "reading";
}

// Pretty portrait card: sacred print + witch-mark result + branding.
export async function buildMarkImageBlob({ markings, printUri, guestName, overall }) {
  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#0b0f0e";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(212,184,114,0.35)";
  ctx.lineWidth = 3;
  ctx.strokeRect(40, 40, W - 80, H - 80);

  ctx.textAlign = "center";
  ctx.fillStyle = "#D4B872";
  ctx.font = "700 26px Georgia, serif";
  ctx.fillText("/ /  S O M A   L I N E A", W / 2, 128);

  const frameW = 540;
  const frameH = 640;
  const fx = (W - frameW) / 2;
  const fy = 168;
  ctx.fillStyle = "#EDE4D1";
  roundRect(ctx, fx, fy, frameW, frameH, 24);
  ctx.fill();
  if (printUri) {
    try {
      const img = await loadImage(printUri);
      const pad = 18;
      const iw = frameW - pad * 2;
      const ih = frameH - pad * 2;
      const r = Math.min(iw / img.width, ih / img.height);
      const dw = img.width * r;
      const dh = img.height * r;
      ctx.drawImage(img, fx + (frameW - dw) / 2, fy + (frameH - dh) / 2, dw, dh);
    } catch {
      /* print unavailable — card still renders */
    }
  }

  const witch = markings?.witch || {};
  ctx.fillStyle = "#C79BD6";
  ctx.font = "italic 700 58px Georgia, serif";
  const titleY = fy + frameH + 88;
  let lastY = wrapText(ctx, witch.title || "The story in my palms", W / 2, titleY, W - 200, 64);

  ctx.fillStyle = "#9a917f";
  ctx.font = "22px Georgia, serif";
  ctx.fillText((witch.subtitle || "A sacred print").toUpperCase(), W / 2, lastY + 50);

  if (overall) {
    ctx.fillStyle = "#EDE4D1";
    ctx.font = "italic 28px Georgia, serif";
    lastY = wrapText(ctx, overall, W / 2, lastY + 108, W - 200, 38);
  } else {
    const who = guestName?.trim() ? `${guestName.trim()}'s reading` : "Read from my own palm";
    ctx.fillStyle = "#EDE4D1";
    ctx.font = "italic 30px Georgia, serif";
    wrapText(ctx, `${who} at Soma Linea.`, W / 2, lastY + 108, W - 220, 40);
  }

  ctx.fillStyle = "#8A938F";
  ctx.font = "20px Georgia, serif";
  ctx.fillText("palmistry as reflection, never prediction", W / 2, H - 78);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}

const INK = [58, 49, 32];
const SOFT = [138, 125, 95];
const ACCENT = [108, 58, 122];

// In-depth multi-section PDF of the full reading.
export function buildReadingPdf({ guestName, prints = [], lines = [], markings, overall, compatibility }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 54;
  const maxW = W - M * 2;
  let y = M;

  const ensure = (need) => {
    if (y + need > H - M) {
      doc.addPage();
      y = M;
    }
  };
  const text = (str, { size = 11, color = INK, style = "normal", gap = 6, lh = 1.4 } = {}) => {
    doc.setFont("times", style);
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const wrapped = doc.splitTextToSize(String(str), maxW);
    wrapped.forEach((ln) => {
      ensure(size * lh);
      doc.text(ln, M, y);
      y += size * lh;
    });
    y += gap;
  };
  const rule = () => {
    ensure(16);
    doc.setDrawColor(210, 198, 170);
    doc.line(M, y, W - M, y);
    y += 16;
  };

  doc.setFont("times", "bold");
  doc.setFontSize(10);
  doc.setTextColor(ACCENT[0], ACCENT[1], ACCENT[2]);
  doc.text("S O M A   L I N E A  ·  A TAKE-HOME READING", M, y);
  y += 24;
  text(guestName?.trim() ? `${guestName.trim()}'s palms` : "The story in your palms", { size: 26, style: "italic", gap: 10 });

  if (prints.length) {
    const imgW = 150;
    const imgH = 190;
    ensure(imgH + 12);
    let x = M;
    prints.slice(0, 2).forEach((p) => {
      try {
        doc.addImage(p.uri, x, y, imgW, imgH);
      } catch {
        /* skip image on failure */
      }
      x += imgW + 16;
    });
    y += imgH + 16;
  }

  const who = guestName?.trim() ? `${guestName.trim()}, thank you` : "Thank you";
  text(`${who} for letting us sit with the story in your palms. What follows isn't a prediction — it's a pattern, offered as an invitation to notice.`, { size: 11, color: SOFT });
  if (overall) text(overall, { size: 13, style: "italic" });

  rule();
  text("The lines", { size: 16, color: ACCENT, gap: 8 });
  lines.forEach((l) => {
    text(`${l.title} · ${l.subtitle}`, { size: 12, style: "bold", gap: 2 });
    text(l.note, { size: 11 });
  });

  if (compatibility) {
    rule();
    text("A Reflection on Resonance", { size: 14, style: "bold", gap: 4 });
    text(compatibility, { size: 11 });
  }

  if (markings?.witch) {
    rule();
    text("Mystic Markings", { size: 16, color: ACCENT, gap: 8 });
    text(`${markings.witch.title} · ${markings.witch.subtitle}`, { size: 12, style: "bold", color: ACCENT, gap: 2 });
    text(markings.witch.text, { size: 11 });
    (markings.gifts || []).forEach((g) => {
      text(`${g.title} · ${g.subtitle}`, { size: 12, style: "bold", color: ACCENT, gap: 2 });
      text(g.text, { size: 11 });
    });
  }

  rule();
  text("Adornments", { size: 16, color: ACCENT, gap: 8 });
  ADORNMENTS.forEach((a) => {
    text(`${a.finger} · ${a.planet} — ${a.energy}`, { size: 12, style: "bold", gap: 2 });
    text(a.note, { size: 11, gap: 2 });
    text(`Stone: ${a.stones} · Metal: ${a.metal}`, { size: 10, color: SOFT });
  });

  rule();
  text(HANDS_GRATITUDE.title, { size: 14, style: "bold", gap: 4 });
  text(HANDS_GRATITUDE.text, { size: 11 });
  text(PALMISTRY_RESPECT.title, { size: 12, style: "bold", gap: 4 });
  text(PALMISTRY_RESPECT.text, { size: 10, color: SOFT });

  rule();
  text("Sit with whatever resonates. Leave the rest. These lines have been forming for a lifetime, and they'll still be here tomorrow — they're yours.", { size: 11, style: "italic" });
  text("With warmth, Soma Linea", { size: 11, color: SOFT });

  doc.save(`soma-linea-${slug(guestName)}.pdf`);
}
